import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, Grid, Card, CardContent,
    Button, Chip, Stack, CircularProgress, Skeleton,
} from '@mui/material';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import LocationOnIcon     from '@mui/icons-material/LocationOn';
import StraightenIcon     from '@mui/icons-material/Straighten';
import EastIcon           from '@mui/icons-material/East';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import axiosPublic from '../api/axiosPublic';
import axiosAuth   from '../api/axiosConfig';
import { useAuth }     from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { useTranslation } from 'react-i18next';

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const WALNUT     = '#2C1F14';
const MIDNIGHT   = '#1A1A2E';

const DIFFICULTY_COLORS = {
    EASY:   { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
    MEDIUM: { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
    HARD:   { bg: '#FFEBEE', color: '#B71C1C', border: '#FFCDD2' },
};

function OfferCardSkeleton() {
    return (
        <Card elevation={0} sx={{ borderRadius: 3, overflow: 'hidden',
            border: '1px solid rgba(201,151,58,0.15)' }}>
            <Skeleton variant="rectangular" height={220} />
            <CardContent>
                <Skeleton variant="text" width="70%" height={28} />
                <Skeleton variant="text" width="45%" height={18} sx={{ mt: 0.5 }} />
                <Stack direction="row" spacing={1} mt={1.5}>
                    <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '20px' }} />
                    <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '20px' }} />
                </Stack>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Skeleton variant="text" width={60} height={32} />
                    <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '20px' }} />
                </Box>
            </CardContent>
        </Card>
    );
}

export default function BikeTravelOfferList() {
    const navigate    = useNavigate();
    const { t }       = useTranslation();
    const { user }    = useAuth();
    const { format: formatMoney } = useCurrency();
    const isManager   = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [offers,  setOffers]  = useState([]);
    const [loading, setLoading] = useState(true);
    const [page,    setPage]    = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const PAGE_SIZE = 9;

    const fetchOffers = async (p = 0) => {
        setLoading(true);
        try {
            const url = isManager
                ? `/api/bike-travel-offers/view/all?page=${p}&size=${PAGE_SIZE}`
                : `/api/bike-travel-offers/view?page=${p}&size=${PAGE_SIZE}`;
            const r = await (isManager ? axiosAuth : axiosPublic).get(url);
            const data = r.data;
            if (p === 0) setOffers(data.content || []);
            else         setOffers(prev => [...prev, ...(data.content || [])]);
            setHasMore(!data.last);
            setPage(p);
        } catch {
            setOffers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchOffers(0); }, []);

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://www.originalarmenia.com/wp-content/uploads/2020/04/armenian-wine-areni.jpg") center/cover fixed` }}>
            <Helmet>
                <title>Cycling Journeys — HayTour</title>
                <meta name="description" content="Multi-day cycling adventures across Armenia. Choose your route, difficulty, and distance." />
            </Helmet>

            {/* ── Page header ─────────────────────────────────────────────── */}
            <Box sx={{ background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                py: { xs: 6, md: 8 }, px: 2, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                {/* Background bike icon watermark */}
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', opacity: 0.04, pointerEvents: 'none' }}>
                    <DirectionsBikeIcon sx={{ fontSize: '28rem', color: GOLD }} />
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="overline"
                                sx={{ color: GOLD, letterSpacing: '0.3em', fontWeight: 700, fontSize: '0.8rem' }}>
                        Multi-day adventures
                    </Typography>
                    <Typography variant="h3" fontWeight={900}
                                sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif",
                                    mt: 1, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                        Cycling Journeys
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 400,
                        maxWidth: 520, mx: 'auto', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        Handcrafted cycling routes across Armenia's mountains, valleys, and ancient roads.
                    </Typography>
                    {isManager && (
                        <Button variant="contained" startIcon={<AddCircleOutlineIcon />}
                                component="a" href="/bike-travel-offers/create"
                                sx={{ mt: 3, background: `linear-gradient(135deg, ${GOLD} 0%, #A07820 100%)`,
                                    color: MIDNIGHT, borderRadius: 2, fontWeight: 800, px: 3, py: 1.2,
                                    boxShadow: '0 4px 20px rgba(201,151,58,0.45)',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 28px rgba(201,151,58,0.55)' },
                                    transition: 'all 0.2s' }}>
                            Create New Journey
                        </Button>
                    )}
                </Box>
            </Box>

            <Container maxWidth="lg" sx={{ py: 6 }}>
                {loading && offers.length === 0 ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}><OfferCardSkeleton /></Grid>
                        ))}
                    </Grid>
                ) : offers.length === 0 ? (
                    <Box sx={{ py: 12, textAlign: 'center', border: `2px dashed rgba(201,151,58,0.2)`,
                        borderRadius: 3 }}>
                        <DirectionsBikeIcon sx={{ fontSize: '3.5rem', color: 'rgba(201,151,58,0.3)', mb: 1.5 }} />
                        <Typography variant="h6" fontWeight={700} sx={{ color: WALNUT, mb: 1 }}>
                            No cycling journeys yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Check back soon for upcoming multi-day rides.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Grid container spacing={3}>
                            {offers.map(offer => {
                                const diff = DIFFICULTY_COLORS[offer.difficulty] || {};
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={offer.id}>
                                        <Card elevation={0}
                                              component="a"
                                              href={`/bike-travel-offers/${offer.id}`}
                                              sx={{ borderRadius: 3, overflow: 'hidden', cursor: 'pointer', textDecoration: 'none', color: 'inherit', display: 'block',
                                                  border: '1px solid rgba(201,151,58,0.15)',
                                                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                  '&:hover': { transform: 'translateY(-8px)',
                                                      boxShadow: '0 20px 48px rgba(26,26,46,0.18)',
                                                      borderColor: 'rgba(201,151,58,0.4)' },
                                                  '&:hover .card-image': { transform: 'scale(1.06)' },
                                                  '&:hover .see-btn': { opacity: 1, transform: 'translateY(0)' },
                                              }}>

                                            {/* Image */}
                                            <Box sx={{ height: 220, overflow: 'hidden', position: 'relative' }}>
                                                {offer.imageUrl ? (
                                                    <Box component="img" src={offer.imageUrl} alt={offer.title}
                                                         className="card-image"
                                                         sx={{ width: '100%', height: '100%', objectFit: 'cover',
                                                             transition: '0.4s ease' }} />
                                                ) : (
                                                    <Box sx={{ width: '100%', height: '100%',
                                                        background: `linear-gradient(135deg, ${MIDNIGHT} 0%, #2A2A4E 100%)`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <DirectionsBikeIcon sx={{ fontSize: '4rem', color: GOLD, opacity: 0.4 }} />
                                                    </Box>
                                                )}
                                                {/* Gradient overlay */}
                                                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                                                    background: 'linear-gradient(to top, rgba(26,26,46,0.6) 0%, transparent 60%)',
                                                    p: 1.5 }}>
                                                    {offer.difficulty && (
                                                        <Chip label={offer.difficulty} size="small"
                                                              sx={{ bgcolor: diff.bg, color: diff.color,
                                                                  border: `1px solid ${diff.border}`,
                                                                  fontWeight: 800, fontSize: '0.68rem' }} />
                                                    )}
                                                </Box>
                                            </Box>

                                            <CardContent sx={{ p: 2.5 }}>
                                                <Typography variant="h6" fontWeight={800}
                                                            sx={{ color: WALNUT, lineHeight: 1.3, mb: 0.8,
                                                                fontFamily: "'Playfair Display', Georgia, serif",
                                                                fontSize: '1.1rem' }}>
                                                    {offer.title}
                                                </Typography>

                                                <Stack direction="row" spacing={1.5} mb={1.5}>
                                                    {offer.region && (
                                                        <Stack direction="row" spacing={0.4} alignItems="center">
                                                            <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                                {offer.region}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    {offer.distance && (
                                                        <Stack direction="row" spacing={0.4} alignItems="center">
                                                            <StraightenIcon sx={{ fontSize: '0.85rem', color: GOLD }} />
                                                            <Typography variant="caption" fontWeight={700} sx={{ color: GOLD }}>
                                                                {offer.distance} km
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                    {offer.duration && (
                                                        <Stack direction="row" spacing={0.4} alignItems="center">
                                                            <Typography variant="caption" fontWeight={700} sx={{ color: FOREST }}>
                                                                {offer.duration}d
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>

                                                {offer.description && (
                                                    <Typography variant="body2" color="text.secondary"
                                                                sx={{ mb: 2, lineHeight: 1.6,
                                                                    display: '-webkit-box', WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {offer.description}
                                                    </Typography>
                                                )}

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                                    alignItems: 'center', mt: 'auto' }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">From</Typography>
                                                        <Typography variant="h6" fontWeight={900}
                                                                    sx={{ color: GOLD, lineHeight: 1.1 }}>
                                                            {formatMoney(offer.basePrice || 0, offer.currency || 'EUR')}
                                                        </Typography>
                                                    </Box>
                                                    <Button className="see-btn" size="small" endIcon={<EastIcon />}
                                                            sx={{ opacity: 0.7, transform: 'translateY(4px)',
                                                                transition: 'all 0.3s ease',
                                                                bgcolor: MIDNIGHT, color: GOLD,
                                                                borderRadius: '20px', fontWeight: 700,
                                                                fontSize: '0.78rem', px: 2, py: 0.8,
                                                                '&:hover': { bgcolor: '#2A2A4E', transform: 'translateY(0) scale(1.04)' } }}>
                                                        View
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {hasMore && (
                            <Box sx={{ textAlign: 'center', mt: 5 }}>
                                <Button variant="outlined" size="large"
                                        onClick={() => fetchOffers(page + 1)}
                                        disabled={loading}
                                        sx={{ borderColor: GOLD, color: GOLD, borderRadius: '30px',
                                            fontWeight: 700, px: 4,
                                            '&:hover': { borderColor: GOLD, bgcolor: 'rgba(201,151,58,0.06)' } }}>
                                    {loading ? <CircularProgress size={20} sx={{ color: GOLD }} /> : 'Load more'}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
}
